/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import { AviBackendErrorMsgPipe } from 'nsx-alb-ui-components';
import { CentralLicenseAlertsService, DevLoggerService } from 'ng/modules/core/services';
import { ComponentStore } from '@ngrx/component-store';
import { CportalService } from 'ajs/modules/cportal';
import { configUpdateDuration } from 'ajs/modules/cportal/constants';
import { Inject, Injectable } from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { LicenseUsage as LicenseUsageClass } from 'items/license-usage.item.factory';
import { LicensingService } from 'ajs/modules/licensing';
import { waitFor } from 'ajs/js/utilities/timer';
import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    IALBServicesAssetDetails,
    LicenseTierType,
} from 'generated-types';

import { defaultApiErrorMessage } from './licensing-page/licensing-page.l10n';

import {
    ILicenseListResponse,
    ILicenseSummary,
    ILicensingStatusResponse,
    IOnPremLicensingDetails,
} from '../licensing.types';

import { LicenseSummaryService } from '../services';

const LICENSE_TYPE_VIRTUAL = 'VIRTUAL';

/**
 * State definition. Contains Licensing data.
 */
interface ICPortalPageStoreState {
    loadingLicenseStatus?: boolean;
    licenseStatus?: ILicensingStatusResponse;
    loadingLicenses?: boolean;
    licenses?: ILicenseListResponse;
    loadingLicenseUsage?: boolean;
    licenseSummary?: ILicenseSummary[];
    reservedServiceCores?: number;
    availableServiceCores?: number;
    usedServiceCores?: number;
    isLicensingServiceConnected?: boolean;
    loadingConnectivityStatus?: boolean;
    assetDetails?: IALBServicesAssetDetails;
    connectivityStatus?: ALBServicesConnectivityStatus;
    registrationStatus?: ALBServicesRegistrationStatus;
    showAddLicense?: boolean;
    isOnPremLicensingEnabled?: boolean;
    tierInUse: LicenseTierType;
    tierChangeInProgress?: boolean;
    onPremLicensingDetails?: IOnPremLicensingDetails;
    error?: string;
}

/** Default state of the Licensing Store */
const initialState: ICPortalPageStoreState = {
    loadingLicenseStatus: false,
    licenseStatus: undefined,
    loadingLicenses: false,
    licenses: undefined,
    loadingLicenseUsage: false,
    licenseSummary: [],
    reservedServiceCores: 0,
    availableServiceCores: 0,
    usedServiceCores: 0,
    isLicensingServiceConnected: false,
    loadingConnectivityStatus: false,
    assetDetails: undefined,
    connectivityStatus: undefined,
    registrationStatus: undefined,
    showAddLicense: false,
    isOnPremLicensingEnabled: false,
    tierInUse: LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES,
    tierChangeInProgress: false,
    onPremLicensingDetails: undefined,
    error: undefined,
};

/**
 * @description
 *     Component store for Licensing Page. Stores Licensing data and exposes
 *     selectors to retrieve properties from their configs. Plain objects as opposed to instances
 *     are stored in the state to follow ngrx convention.
 * @author Deepak Chaudhary, Shubam Bindal
 */
@Injectable()
export class LicensingPageStore extends ComponentStore<ICPortalPageStoreState> {
    public readonly licenseList$ = this.select(
        state => state?.licenses?.licenses?.filter(
            license => license?.license_type?.toUpperCase() !== LICENSE_TYPE_VIRTUAL,
        ),
    );
    public readonly licenseStatus$ = this.select(state => state?.licenseStatus);
    public readonly saasStatus$ = this.select(state => state?.licenseStatus?.saas_status);
    public readonly clsStatus$ = this.select(state => state?.licenseStatus?.cls_status);
    public readonly serviceUnits$ = this.select(state => state?.licenseStatus?.service_update?.service_units);
    public readonly licensePool$ = this.select(state => state?.licenseStatus?.saas_status?.pool);
    public readonly legacyLicenseGracePeriod$ = this.select(state => state?.licenseStatus?.legacy_license_grace_period);
    public readonly availableServiceCores$ = this.select(state => state?.availableServiceCores);
    public readonly reservedServiceCores$ = this.select(state => state?.reservedServiceCores);
    public readonly usedServiceCores$ = this.select(state => state?.usedServiceCores);
    public readonly loadingLicenseUsage$ = this.select(state => state?.loadingLicenseUsage);
    public readonly loadingLicenses$ = this.select(state => state?.loadingLicenses);
    public readonly licenseSummary$ = this.select(state => state?.licenseSummary);
    public readonly isLicensingServiceConnected$ = this.select(state => state?.isLicensingServiceConnected);
    public readonly connectivityStatus$ = this.select(state => state?.connectivityStatus);
    public readonly registrationStatus$ = this.select(state => state?.registrationStatus);
    public readonly loadingLicenseStatus$ = this.select(state => state?.loadingLicenseStatus);
    public readonly assetDetails$ = this.select(state => state?.assetDetails);
    public readonly showAddLicense$ = this.select(state => state?.licenseStatus?.support_legacy_license);
    public readonly tierInUse$ = this.select(state => state?.tierInUse);
    public readonly isOnPremLicensingEnabled$ = this.select(
        state => state?.licenseStatus?.cls_status?.enabled,
    );
    public readonly tierChangeInProgress$ = this.select(state => state?.tierChangeInProgress);
    public readonly onPremLicensingDetails$ = this.select(state => state?.onPremLicensingDetails);
    public readonly error$ = this.select(state => state?.error);

    /**
     * State is initialized with System Config.
     */
    constructor(
        private readonly aviBackendErrorMsgPipe: AviBackendErrorMsgPipe,
        private readonly devLoggerService: DevLoggerService,
        private readonly l10nService: L10nService,
        private readonly licensingService: LicensingService,
        private readonly licenseSummaryService: LicenseSummaryService,
        private readonly centralLicenseAlertsService: CentralLicenseAlertsService,
        private readonly cPortalService: CportalService,
        @Inject(LicenseUsageClass)
        private readonly LicenseUsage: typeof LicenseUsageClass,
    ) {
        super(initialState);
    }

    public setSelectedLicensingTier(tierInUse: LicenseTierType): void {
        this.patchState({ tierInUse });
    }

    public setTierChangeInProgress(tierChangeInProgress: boolean): void {
        this.patchState({ tierChangeInProgress });
    }

    public setLicensingServiceConnected(isLicensingServiceConnected: boolean): void {
        this.patchState({ isLicensingServiceConnected });
    }

    public setError(error: string): void {
        this.patchState({ error });
    }

    public clearError(): void {
        this.patchState({ error: undefined });
    }

    public async loadLicenses(): Promise<void> {
        this.patchState({ loadingLicenses: true });

        try {
            const licenses = await this.licensingService.loadLicenseList();

            this.patchState({ licenses });
        } catch (error) {
            this.handleError(error);
            throw error;
        } finally {
            this.patchState({ loadingLicenses: false });
        }
    }

    /**
     * Load licensing status from Licensing Service.
     */
    public async loadLicenseStatus(): Promise<void> {
        this.patchState({ loadingLicenseStatus: true });

        try {
            const licenseStatus = await this.licensingService.loadLicenseStatus();

            this.patchState({ licenseStatus });
        } catch (error) {
            this.handleError(error);
            throw error;
        } finally {
            this.patchState({ loadingLicenseStatus: false });
        }
    }

    public async loadLicenseUsage(defaultLicenseTier: LicenseTierType, reserveServiceUnits: number): Promise<void> {
        const licenseUsage = new this.LicenseUsage();

        this.patchState({ loadingLicenseUsage: true });

        try {
            await licenseUsage.load();

            const {
                licenseTierUsageData,
                licenseHash,
                licenseTypes,
                licenseTierTypes,
            } = licenseUsage.config;

            const licenseSummary = this.licenseSummaryService
                .getLicenseListByType(licenseHash, licenseTypes, licenseTierTypes);

            let reservedServiceCores = licenseTierUsageData[defaultLicenseTier]?.reserved_service_cores ?? 0;
            const availableServiceCores = licenseTierUsageData[defaultLicenseTier]?.licensed_service_cores ?? 0;
            const usedServiceCores = licenseSummary[0]?.tiers[defaultLicenseTier]?.used ?? 0;

            if (reserveServiceUnits) {
                reservedServiceCores = reserveServiceUnits;
            }

            this.patchState({
                licenseSummary,
                reservedServiceCores,
                availableServiceCores,
                usedServiceCores,
            });
        } catch (error) {
            this.handleError(error);
            throw error;
        } finally {
            licenseUsage.destroy();
            this.patchState({ loadingLicenseUsage: false });
        }
    }

    /**
     * Delete a license by its id.
     * @returns Promise that resolves when the license is deleted
     */
    public async deleteLicense(licenseId: string): Promise<void> {
        try {
            await this.licensingService.deleteLicense(licenseId);
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    public async loadConnectivityStatus(): Promise<void> {
        try {
            this.patchState({ loadingConnectivityStatus: true });

            await this.cPortalService.loadRegistrationStatus();

            const albServiceStatus = this.cPortalService.getRegistrationStatusDetails();
            const {
                asset_details: assetDetails,
                connectivity_status: connectivityStatus,
                registration_status: registrationStatus,
            } = albServiceStatus;

            this.centralLicenseAlertsService.controllerRegistrationStatus = registrationStatus;

            this.patchState({
                assetDetails,
                connectivityStatus,
                registrationStatus,
            });
        } catch (error) {
            this.handleError(error);
            throw error;
        } finally {
            this.patchState({ loadingConnectivityStatus: false });
        }
    }

    /**
     * Unsubscribe from cloud licensing.
     */
    public async unsubscribeCloudLicensing(): Promise<void> {
        try {
            await this.licensingService.unsubscribeCloudLicensing();
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Unsubscribe from on prem licensing.
     */
    public async unsubscribeOnPremLicensing(clsUuid: string): Promise<void> {
        try {
            await this.licensingService.unsubscribeOnPremLicensing(clsUuid);
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Refresh licenses for on Prem Licensing.
     */
    public async refreshClsLicenses(): Promise<void> {
        try {
            await this.licensingService.refreshClsLicenses();
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Load on-prem licensing details from License Hub.
     */
    public async loadOnPremLicensingDetails(clsUuid: string): Promise<void> {
        try {
            const onPremLicensingDetails = await this.licensingService.getOnPremLicensingDetails(clsUuid);

            this.patchState({ onPremLicensingDetails });
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Deregister from cloud services.
     */
    public async deregisterCloudServices(): Promise<void> {
        try {
            await this.cPortalService.deregisterController();
            await waitFor(configUpdateDuration);
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Unsubscribe from cloud licensing and optionally deregister from cloud services.
     * @param shouldDeregisterCloudServices - Whether to also deregister from cloud services
     */
    public async disconnectCloudLicensing(shouldDeregisterCloudServices: boolean): Promise<void> {
        try {
            await this.unsubscribeCloudLicensing();

            if (shouldDeregisterCloudServices) {
                await this.deregisterCloudServices();
            }
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Disconnect on-prem License Hub.
     */
    public async disconnectOnPremLicensing(clsUuid: string): Promise<void> {
        try {
            await this.unsubscribeOnPremLicensing(clsUuid);
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Clear the registration state when controller is connected but not yet registered.
     */
    public async clearRegistration(): Promise<void> {
        try {
            await this.cPortalService.clearRegistration();
        } catch (error) {
            this.devLoggerService.error(error);
            throw error;
        }
    }

    /**
     * Handle error by logging it and storing the error message in state.
     * @param error - Error object or string to handle
     */
    private handleError(error: unknown): void {
        const errorMessage = this.extractErrorMessage(error);

        this.devLoggerService.error(error);
        this.setError(errorMessage);
    }

    /**
     * Extract error message from various error object formats.
     * Use AviBackendErrorMsgPipe which handles nested error structures.
     */
    private extractErrorMessage(error: unknown): string {
        const defaultErrorMessage = this.l10nService.getMessage(defaultApiErrorMessage);

        if (typeof error === 'string') {
            return error || defaultErrorMessage;
        }

        if (!error || typeof error !== 'object') {
            return defaultErrorMessage;
        }

        const errorObj = error as {
            error?: string;
            message?: string;
            data?: {
                error?: string;
                message?: string;
            };
        };

        const errorFromPipe = this.aviBackendErrorMsgPipe.transform(errorObj?.data as string) ||
            this.aviBackendErrorMsgPipe.transform(errorObj as string);

        if (errorFromPipe) {
            return errorFromPipe;
        }

        return defaultErrorMessage;
    }
}
